import { Dispatch, SetStateAction } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Theme, Typography } from '@mui/material';
import clsx from 'clsx';
import { ReactComponent as LogoImage } from './sign-logo.svg';
import Loader from '../../../components/Loader';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { FIRM_ROLES } from '../../CasesView/types';
import { SettlementAdditionalInfo, SettlementPaymentPreview } from '../../../types';

const useStyles = makeStyles()((theme: Theme) => ({
  feesSummaryWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  feesSummaryContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    background: theme.palette.primary.light,
    minHeight: '350px',
    borderRadius: '16px',

    '@media (max-width: 650px)': {
      padding: '20px',
    },
  },
  feesSummaryTotalGrossHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    padding: '10px 8px 10px 10px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Poppins',
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'normal',
    },
  },
  feesSummarySectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  feesSummarySectionContainerWithhold: {
    display: 'flex',
    flexDirection: 'column',
  },
  feesSummarySectionHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 8px 2px 10px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Poppins',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'normal',
    },
  },
  feesSummarySectionInnerHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 8px 0 34px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
    },
  },
  feesSummaryBlockDivider: {
    height: '1px',
    background: theme.palette.secondary.dark,
    margin: '0 0 4px 10px',
  },
  feesSummaryBlockDividerFull: {
    height: '1px',
    background: theme.palette.primary.main,
    marginTop: '10px',
    marginBottom: '10px',
    marginLeft: '-20px',
    marginRight: '-20px',
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    marginTop: '80px',
  },
  noCheckTextList: {
    listStyleType: 'disc',
    padding: 0,
    paddingLeft: '30px',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Poppins',
    fontSize: '16px',
    gap: '3px',
    color: theme.palette.common.white,
  },

  // Inline styles moved to classes
  noCheckContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '130px',
    gap: '5px',
    alignSelf: 'center',
  },
  paidStatusContainer: {
    display: 'flex',
    gap: '5px',
    flexDirection: 'column',
  },
  statusText: {
    color: 'white',
    fontFamily: 'Poppins',
    fontSize: '16px',
    lineHeight: '20px',
  },
  errorMessageText: {
    color: 'white',
    fontFamily: 'Poppins',
    fontSize: '16px',
    lineHeight: '20px',
  },
  printHeaderHidden: {
    display: 'none',
  },
  sectionMarginTop: {
    marginTop: '20px',
  },
  sectionsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  wave1Title: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  wave1Amount: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  subtitleContainer: {
    padding: '0 10px',
    marginBottom: '10px',
  },
  subtitleText: {
    fontFamily: 'Poppins',
    fontSize: '11px',
    fontWeight: 400,
    color: theme.palette.text.disabled,
  },
  sectionTitle: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  sectionAmount: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  itemLabel: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  itemAmount: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  itemTextWrapper: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
  },
  itemSpanLabel: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  itemSpanSuccess: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.success.main,
  },
  amountAligned: {
    minWidth: '120px',
    textAlign: 'right',
    flexShrink: 0,
  },
}));

interface SettlementsPayoutCheckProps {
  isPayoutCheckUpdating?: boolean;
  payoutCheck?: SettlementPaymentPreview;
  setPayoutCheck?: Dispatch<SetStateAction<SettlementPaymentPreview | undefined>>;
  setAdditionalSettlementInfo?: Dispatch<SetStateAction<SettlementAdditionalInfo | undefined>>;
  secondaryFirmsData: { id: string; name: string; role: string }[] | unknown;
  caseSystemName: string | unknown;
  leadFirmData?: { id: string; name: string } | unknown;
  setIsPayoutCheckUpdating: Dispatch<SetStateAction<boolean>>;
  status?: string;
  intentType?: string;
  errorMessage?: string;
}

const SettlementsPayoutCheck = ({
  isPayoutCheckUpdating,
  payoutCheck,
  caseSystemName,
  secondaryFirmsData,
  leadFirmData,
  errorMessage,
}: SettlementsPayoutCheckProps) => {
  const styles = useStyles();

  const leadFirmName: { id: string; name: string } = leadFirmData as { id: string; name: string };
  const caseName: string = caseSystemName as string;
  const secondaryFirms: { id: string; name: string; role: string }[] = secondaryFirmsData as {
    id: string;
    name: string;
    role: string;
  }[];

  const sortedSecondaryFirms = secondaryFirms
    ?.sort((a, b) => a.role.localeCompare(b.role))
    .map((firm) => ({
      firmName: firm.name,
      firmRole: FIRM_ROLES[firm.role as keyof typeof FIRM_ROLES],
    }));

  // Get the distribution with the highest sequence number
  const currentDistribution = payoutCheck?.distributions?.reduce((prev, current) =>
    (current.sequence > prev.sequence) ? current : prev
  );

  // Sort distributions by sequence and separate previous waves from current
  const sortedDistributions = payoutCheck?.distributions
    ? [...payoutCheck.distributions].sort((a, b) => a.sequence - b.sequence)
    : [];
  const previousWaves = sortedDistributions.filter(
    (dist) => dist.sequence !== currentDistribution?.sequence
  );
  const hasPreviousWaves = previousWaves.length > 0;

  if (isPayoutCheckUpdating) {
    return (
      <Box className={styles.classes.feesSummaryContainer}>
        <Box className={styles.classes.loaderContainer}>
          <Loader colorType="info" />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={styles.classes.feesSummaryWrapper}>
        <Box className={styles.classes.feesSummaryContainer} >
          {payoutCheck === undefined ? (
            <Box className={styles.classes.noCheckContainer}>
              <Typography className={styles.classes.errorMessageText}>
                {errorMessage}
              </Typography>
              {/* {status && status === 'Paid' ? (
                <Box className={styles.classes.paidStatusContainer}>
                  <Typography className={styles.classes.statusText}>
                    Payment preview unavailable.
                  </Typography>
                  <Typography className={styles.classes.statusText}>
                    The settlement has been paid.
                  </Typography>
                </Box>
              ) : errorMessage ? (
                <Typography className={styles.classes.errorMessageText}>
                  {errorMessage}
                </Typography>
              ) : (
                <>
                  <Typography className={styles.classes.errorMessageText}>
                    Payment preview unavailable. Please review and update:
                  </Typography>
                  {status && status !== 'Paid' && (
                    <ul className={styles.classes.noCheckTextList}>
                      <li>Participating law firms</li>
                      <li>Defendants and gross settlement amounts</li>
                      <li>Attorneys’ Fees</li>
                      <li>Trust account</li>
                    </ul>
                  )}
                </>
              )} */}
            </Box>
          ) : (
            <Box className="feesSummaryContainer">
              <Box className={clsx('print-header', styles.classes.printHeaderHidden)}>
                <Box className="logo-image-wrapper">
                  <LogoImage />
                </Box>
                <Typography className="print-case-system-name">{caseName}</Typography>
                <Typography>{`${leadFirmName} (${FIRM_ROLES.LeadCounsel})`}</Typography>
                {sortedSecondaryFirms?.map((firm) => (
                  <Typography
                    key={firm.firmName}
                  >{`${firm.firmName} (${firm.firmRole})`}</Typography>
                ))}
              </Box>

              {/* Print-only: Total Settlement Amount row above Wave section */}
              <Box
                className={clsx(
                  styles.classes.feesSummaryTotalGrossHeadline,
                  'print-feesSummaryTotalGrossHeadline',
                  'show-in-print-only'
                )}
                style={{ display: 'none' }}
              >
                <Typography className={styles.classes.wave1Title}>
                  Total Settlement Amount
                </Typography>
                <Typography className={clsx(styles.classes.wave1Amount, styles.classes.amountAligned)}>
                  {numberToUSDCurrency(payoutCheck?.total)}
                </Typography>
              </Box>

              {/* Print-only: Previous Waves rows below Total Settlement Amount */}
              {hasPreviousWaves && previousWaves.map((wave) => (
                <Box
                  key={wave.sequence}
                  className={clsx(
                    styles.classes.feesSummaryTotalGrossHeadline,
                    'print-feesSummaryTotalGrossHeadline',
                    'show-in-print-only'
                  )}
                  style={{ display: 'none' }}
                >
                  <Typography className={styles.classes.wave1Title}>
                    Wave {wave.sequence} – Settled Amount
                  </Typography>
                  <Typography className={clsx(styles.classes.wave1Amount, styles.classes.amountAligned)}>
                    -{numberToUSDCurrency(wave.currentDisbursement ?? 0)}
                  </Typography>
                </Box>
              ))}

              {hasPreviousWaves && (
                <Box
                  className={clsx(
                    styles.classes.feesSummaryTotalGrossHeadline,
                    'print-feesSummaryTotalGrossHeadline',
                    'show-in-print-only'
                  )}
                  style={{ display: 'none' }}
                >
                  <Typography className={styles.classes.wave1Title}>
                    Balance Remaining
                  </Typography>
                  <Typography className={clsx(styles.classes.wave1Amount, styles.classes.amountAligned)}>
                    {numberToUSDCurrency(payoutCheck?.balanceRemaining ?? 0)}
                  </Typography>
                </Box>
              )}

              <Box className="print-only-divider"></Box>

              {/* ===== Wave {sequence}-Settled Amount ===== */}
              <Box
                className={clsx(
                  styles.classes.feesSummaryTotalGrossHeadline,
                  'print-feesSummaryWaveHeadline',
                )}
              >
                <Typography className={styles.classes.wave1Title}>
                  Wave {currentDistribution?.sequence} – Settled Amount
                </Typography>
                <Typography className={clsx(styles.classes.wave1Amount, styles.classes.amountAligned)}>
                  -{numberToUSDCurrency(payoutCheck?.currentDisbursement || 0)}
                </Typography>
              </Box>

              {/* Subtitle */}
              <Box className={clsx(styles.classes.subtitleContainer, 'print-feesSummarySectionInnerHeadline')}>
                <Typography className={styles.classes.subtitleText}>
                  Settled amount will be withdrawn from the IOLTA account.
                </Typography>
              </Box>

              {/* Full-width divider after Wave 1 */}
              <Box
                className={clsx(
                  styles.classes.feesSummaryBlockDividerFull,
                  'print-feesSummaryBlockDivider',
                )}
              ></Box>

              {/* Sections Stack - Contains all major sections with consistent spacing */}
              <Box className={styles.classes.sectionsStack}>
                {/* ===== Attorneys' Fees Section ===== */}
                {(currentDistribution?.attorneysFees?.totalPaid ?? 0) > 0 && (
                  <Box
                    className={clsx(
                      styles.classes.feesSummarySectionContainer,
                      'print-feesSummarySectionContainer',
                    )}
                  >
                    {/* Section heading */}
                    {currentDistribution?.attorneysFees?.totalPaid &&
                      currentDistribution.attorneysFees.totalPaid > 0 && (
                        <>
                          <Box
                            className={clsx(
                              styles.classes.feesSummarySectionHeadline,
                              'print-feesSummarySectionHeadline',
                            )}
                          >
                            <Typography className={styles.classes.sectionTitle}>
                              Attorneys’ Fees
                            </Typography>
                            <Typography className={clsx(styles.classes.sectionAmount, styles.classes.amountAligned)}>
                              -{numberToUSDCurrency(currentDistribution?.attorneysFees?.totalPaid || 0)}
                            </Typography>
                          </Box>

                          {/* Divider right after Attorneys' Fees heading */}
                          <Box
                            className={clsx(
                              styles.classes.feesSummaryBlockDivider,
                              'print-feesSummaryBlockDivider',
                            )}
                          ></Box>
                        </>
                      )}

                    {/* Lead firm sub-item - only show if exists and has paid amount > 0 */}
                    {currentDistribution?.attorneysFees?.leadFirm?.name &&
                      currentDistribution?.attorneysFees?.leadFirm?.totalPaid &&
                      currentDistribution.attorneysFees.leadFirm.totalPaid > 0 && (
                        <Box
                          className={clsx(
                            styles.classes.feesSummarySectionInnerHeadline,
                            'print-feesSummarySectionInnerHeadline',
                          )}
                        >
                          <Typography className={styles.classes.itemLabel}>
                            {currentDistribution.attorneysFees.leadFirm.name} - {FIRM_ROLES.LeadCounsel}
                          </Typography>
                          <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                            -{numberToUSDCurrency(currentDistribution.attorneysFees.leadFirm.totalPaid)}
                          </Typography>
                        </Box>
                      )}

                    {/* Co-counsel sub-item - only show if totalPaid > 0 */}
                    {(currentDistribution?.attorneysFees?.secondaryFirmsCoCounselTotalPaid ?? 0) > 0 && (
                      <Box
                        className={clsx(
                          styles.classes.feesSummarySectionInnerHeadline,
                          'print-feesSummarySectionInnerHeadline',
                        )}
                      >
                        <Typography className={styles.classes.itemLabel}>
                          {FIRM_ROLES.CoCounsel}
                        </Typography>
                        <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                          -{numberToUSDCurrency(currentDistribution?.attorneysFees.secondaryFirmsCoCounselTotalPaid)}
                        </Typography>
                      </Box>
                    )}

                    {/* Referring Counsel sub-item - only show if totalPaid > 0 */}
                    {(currentDistribution?.attorneysFees?.secondaryFirmsReferingCounselTotalPaid ?? 0) > 0 && (
                      <Box
                        className={clsx(
                          styles.classes.feesSummarySectionInnerHeadline,
                          'print-feesSummarySectionInnerHeadline',
                        )}
                      >
                        <Typography className={styles.classes.itemLabel}>
                          {FIRM_ROLES.ReferringCounsel}
                        </Typography>
                        <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                          -{numberToUSDCurrency(currentDistribution?.attorneysFees.secondaryFirmsReferingCounselTotalPaid)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* ===== Deductions Section ===== */}
                {currentDistribution?.deductions && (
                  <Box
                    className={clsx(
                      styles.classes.feesSummarySectionContainer,
                      'print-feesSummarySectionContainer',
                    )}
                  >
                    {/* Section heading */}
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionHeadline,
                        'print-feesSummarySectionHeadline',
                      )}
                    >
                      <Typography className={styles.classes.sectionTitle}>
                        Deductions
                      </Typography>
                      <Typography className={clsx(styles.classes.sectionAmount, styles.classes.amountAligned)}>
                        -{numberToUSDCurrency(currentDistribution?.deductions?.totalPaid || 0)}
                      </Typography>
                    </Box>

                    {/* Divider right after Deductions heading */}
                    <Box
                      className={clsx(
                        styles.classes.feesSummaryBlockDivider,
                        'print-feesSummaryBlockDivider',
                      )}
                    ></Box>

                    {/* Liens */}
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionInnerHeadline,
                        'print-feesSummarySectionInnerHeadline',
                      )}
                    >
                      <Typography className={styles.classes.itemLabel}>
                        Liens
                      </Typography>
                      <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                        -{numberToUSDCurrency(currentDistribution?.deductions?.liens || 0)}
                      </Typography>
                    </Box>

                    {/* Liens - Paid Externally
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionInnerHeadline,
                        'print-feesSummarySectionInnerHeadline',
                      )}
                    >
                      <Typography className={styles.classes.itemTextWrapper}>
                        <Typography
                          component="span"
                          className={styles.classes.itemSpanLabel}
                        >
                          Liens -{' '}
                        </Typography>
                        <Typography
                          component="span"
                          className={styles.classes.itemSpanError}
                        >
                          Paid Externally
                        </Typography>
                      </Typography>
                      <Typography className={styles.classes.itemAmount}>
                        {'- $500.00'}
                      </Typography>
                    </Box> */}

                    {/* Services Provider */}
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionInnerHeadline,
                        'print-feesSummarySectionInnerHeadline',
                      )}
                    >
                      <Typography className={styles.classes.itemLabel}>
                        Services Provider
                      </Typography>
                      <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                        -{numberToUSDCurrency(currentDistribution?.deductions?.serviceProviders || 0)}
                      </Typography>
                    </Box>

                    {/* Expenses */}
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionInnerHeadline,
                        'print-feesSummarySectionInnerHeadline',
                      )}
                    >
                      <Typography className={styles.classes.itemLabel}>
                        Expenses
                      </Typography>
                      <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                        -{numberToUSDCurrency(currentDistribution?.deductions?.advances || 0)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* ===== Net Settlement to Client Section ===== */}
                {(() => {
                  const negotiationSavings = [
                    {
                      label: 'Liens',
                      value: currentDistribution?.deductions?.liensNegotiationSavings,
                    },
                    {
                      label: 'Service Provider',
                      value: currentDistribution?.deductions?.serviceProvidersNegotiationSavings,
                    },
                    {
                      label: 'Expenses',
                      value: currentDistribution?.deductions?.advancesNegotiationSavings,
                    },
                  ].filter((item) => typeof item.value === 'number' && item.value > 0);

                  return (
                    <Box
                      className={clsx(
                        styles.classes.feesSummarySectionContainer,
                        'print-feesSummarySectionContainer',
                      )}
                    >
                      {/* Section heading */}
                      <Box
                        className={clsx(
                          styles.classes.feesSummarySectionHeadline,
                          'print-feesSummarySectionHeadline',
                        )}
                      >
                        <Typography className={styles.classes.sectionTitle}>
                          Net Settlement to Client
                        </Typography>
                        <Typography className={clsx(styles.classes.sectionAmount, styles.classes.amountAligned)}>
                          -{numberToUSDCurrency(currentDistribution?.netSettlement?.amount || 0)}
                        </Typography>
                      </Box>

                      {/* Divider - always shown */}
                      <Box
                        className={clsx(
                          styles.classes.feesSummaryBlockDivider,
                          'print-feesSummaryBlockDivider',
                        )}
                      ></Box>

                      {/* Initial Net Settlement to Client - always shown */}
                      <Box
                        className={clsx(
                          styles.classes.feesSummarySectionInnerHeadline,
                          'print-feesSummarySectionInnerHeadline',
                        )}
                      >
                        <Typography className={styles.classes.itemLabel}>
                          Initial Net Settlement to Client
                        </Typography>
                        <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                          -{numberToUSDCurrency(currentDistribution?.netSettlement?.nonNegotiatedAmountToClient || 0)}
                        </Typography>
                      </Box>

                      {/* Negotiation Savings Rows - only shown when they exist */}
                      {negotiationSavings.length > 0 &&
                        negotiationSavings.map((item) => (
                          <Box
                            key={item.label}
                            className={clsx(
                              styles.classes.feesSummarySectionInnerHeadline,
                              'print-feesSummarySectionInnerHeadline',
                            )}
                          >
                            <Typography className={styles.classes.itemTextWrapper}>
                              <Typography
                                component="span"
                                className={styles.classes.itemSpanLabel}
                              >
                                {item.label} -{' '}
                              </Typography>
                              <Typography
                                component="span"
                                className={styles.classes.itemSpanSuccess}
                              >
                                Saved by negotiation
                              </Typography>
                            </Typography>
                            <Typography className={clsx(styles.classes.itemAmount, styles.classes.amountAligned)}>
                              +{numberToUSDCurrency(Math.abs(item.value || 0))}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  );
                })()}
              </Box>

              {/* <Box className={clsx('print-date-time', styles.classes.printHeaderHidden)}>
                <Typography>{`Generated at ${currentDateTime[0]} on ${currentDateTime[1]}`}</Typography>
              </Box> */}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SettlementsPayoutCheck;